import { FC, useContext, useEffect, useMemo, useState } from "react";
import { TaskContext } from "../../../contexts/TaskContext";
import IntegrationTasksTree, { ExternalPlanElementExtended } from "../IntegrationTasksTree/IntegrationTasksTree";
import ProjectService from "../../../services/ProjectService";
import { isAxiosError } from "axios";
import { useUI } from "../../../contexts/UiContext";
import {
    ExternalPlanElement,
    ExternalPlanElementTypes,
} from "../../../common/api_interfaces/project/ExternalPlanElement";
import { dateToNiceString } from "../../../helpers/dateToNiceString";
import "./TaskIntegrationTab.css";

interface TaskIntegrationTabParams {
    canEdit?: boolean,
}

const TaskIntegrationTab: FC<TaskIntegrationTabParams> = ({canEdit}) => {
    const {
        taskInformation,
        loadTaskInfo,
        forceLinkDescription,
        forceLinkExecutor,
        forceLinkName,
        forceLinkTerm,
    } = useContext(TaskContext);
    const { showToast } = useUI();
    const [tasks, setTasks] = useState<ExternalPlanElementExtended[]>();

    const anyForceLinks = useMemo(
        () => forceLinkDescription || forceLinkExecutor || forceLinkName || forceLinkTerm,
        [forceLinkDescription, forceLinkExecutor, forceLinkName, forceLinkTerm]
    );

    const forceLinkString = useMemo(() => {
        const strArr: string[] = [];
        forceLinkDescription && strArr.push("Описание");
        forceLinkName && strArr.push("Название");
        forceLinkExecutor && strArr.push("Исполнитель");
        forceLinkTerm && strArr.push("Сроки");
        return strArr.join(", ");
    }, [forceLinkDescription, forceLinkExecutor, forceLinkName, forceLinkTerm]);

    const [tasksModalOpen, setTasksModalOpen] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            try {
                if (!taskInformation) return;
                const project = await ProjectService.getProjectGeneralInfo(taskInformation.projectId);
                if (project.data.externalPlan) setTasks(buildPlanTree(project.data.externalPlan));
            } catch (err) {
                if (isAxiosError(err)) showToast(err.response?.data.message);
            }
        })();
    }, [taskInformation]);

    const onLinkTask = () => {
        loadTaskInfo();
        setTasksModalOpen(false);
    };

    function buildPlanTree(items: ExternalPlanElement[]): ExternalPlanElementExtended[] {
        const map = new Map<string, ExternalPlanElementExtended>();

        // создаём расширенные ноды
        for (const item of items) {
            map.set(item.elementId, { ...item, subtasks: [] });
        }

        const roots: ExternalPlanElementExtended[] = [];

        // связываем детей с родителями
        map.forEach((item) => {
            if (item.parentElementId) {
                const parent = map.get(item.parentElementId);
                if (parent) {
                    parent.subtasks.push(item);
                } else {
                    roots.push(item);
                }
            } else {
                roots.push(item);
            }
        });

        const sortTree = (nodes: ExternalPlanElementExtended[]) => {
            nodes.sort((a, b) => a.elementOrder - b.elementOrder);
            nodes.forEach((n) => sortTree(n.subtasks));
        };

        sortTree(roots);

        return roots;
    }

    return (
        <div>
            {tasksModalOpen && (
                <div className="full-screen-overlay" onClick={() => setTasksModalOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()}>
                        <IntegrationTasksTree
                            tasks={tasks ?? []}
                            onClose={() => setTasksModalOpen(false)}
                            onLink={onLinkTask}
                        />
                    </div>
                </div>
            )}
            {taskInformation?.externalTask !== undefined ? (
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="task-name">{taskInformation.externalTask.elementName}</div>
                        {/* <div
                            className={`task__create--edit__form--tab__panel--button__add ${
                                anyForceLinks ? "disabled" : ""
                            }`}
                            style={{ width: "fit-content" }}
                        >
                            <button
                                style={{ background: "none", border: "none" }}
                                onClick={() =>
                                    anyForceLinks
                                        ? showToast("Задача синхронизирована с внешней системой")
                                        : setTasksModalOpen(true)
                                }
                            >
                                Изменить
                            </button>
                        </div> */}
                    </div>
                    <div>
                        <span className="parameter-header">Тип: </span>
                        <span className="parameter-value">
                            {taskInformation.externalTask.elementType === ExternalPlanElementTypes.task
                                ? "Задача"
                                : "Фазовая задача"}
                        </span>
                    </div>
                    <div>
                        <span className="parameter-header">Статус: </span>
                        <span className="parameter-value">{taskInformation.externalTask.task?.status}</span>
                    </div>
                    <div>
                        <span className="parameter-header">Исполнитель: </span>
                        <span className="parameter-value">{taskInformation.externalTask.task?.executor?.name}</span>
                    </div>
                    <div>
                        <span className="parameter-header">Дата начала: </span>
                        <span className="parameter-value">
                            {taskInformation.externalTask.task?.start &&
                                dateToNiceString(new Date(taskInformation.externalTask.task.start), "dd.MM.yy")}
                        </span>
                    </div>
                    <div>
                        <span className="parameter-header">Дата окончания: </span>
                        <span className="parameter-value">
                            {taskInformation.externalTask.task?.end &&
                                dateToNiceString(new Date(taskInformation.externalTask.task.end), "dd.MM.yy")}
                        </span>
                    </div>
                    <div>
                        <span className="parameter-header">Плановые трудозатраты: </span>
                        <span className="parameter-value">
                            {taskInformation.externalTask.task?.costs !== undefined &&
                                `${Math.floor(taskInformation.externalTask.task.costs / 60)
                                    .toString()
                                    .padStart(2, "0")}:${(taskInformation.externalTask.task.costs % 60)
                                    .toString()
                                    .padStart(2, "0")}`}
                        </span>
                    </div>
                    <div>
                        <span className="parameter-header">Прогресс: </span>
                        <span className="parameter-value">
                            {taskInformation.externalTask.task?.progress
                                ? taskInformation.externalTask.task?.progress + "%"
                                : ""}
                        </span>
                    </div>
                    {anyForceLinks ? (
                        <div>
                            <br></br>
                            <span className="parameter-header">Синхронизация: </span>
                            <span className="parameter-value">{forceLinkString}</span>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="empty-task-container">
                    <h1 className="link-task-header">Нет внешней задачи</h1>
                    {canEdit ? (
                        <div
                            className={`task__create--edit__form--tab__panel--button__add ${
                                anyForceLinks ? "disabled" : ""
                            }`}
                            style={{ width: "fit-content" }}
                        >
                            <button
                                onClick={() =>
                                    anyForceLinks
                                        ? showToast("Задача синхронизирована с внешней системой")
                                        : setTasksModalOpen(true)
                                }
                                style={{ background: "none", border: "none", width: "fit-content" }}
                            >
                                Привязать
                            </button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default TaskIntegrationTab;
